import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import api from '@/shared/api/api';
import { AgeGroup } from '@/shared/api/protocol-ts/api/core/svc_patient_pb';
import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import { useAppSelector, useDebounce } from '@/shared/hooks';
import { debug, getName } from '@/shared/lib';
import { Input, Modal, Spinner } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { AttachPatientArgs } from '../../../../hooks';

import styles from './AttachPatientModal.module.scss';

type AttachPatientModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAttachPatient: (args: AttachPatientArgs) => Promise<void>;
};

const usePatientSearch = (
  organizationID: string | undefined,
  query: string,
) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!organizationID) {
      setPatients([]);
      return undefined;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    const load = async () => {
      try {
        const stream = api.patient.patientListStream(
          {
            OrganizationID: organizationID,
            SearchString: query,
            Doctors: { case: 'AllDoctors', value: {} },
            Studies: { case: 'AllStudyTypes', value: {} },
            Age: AgeGroup.PatientListStreamAgeGroup_AllAges,
            StartFromPatientID: '',
            Limit: 20,
          },
          { signal: controller.signal },
        );

        for await (const { Update } of stream) {
          if (Update.case === 'InitialPatientsListItems') {
            const list = (Update.value?.Patients ?? [])
              .map(({ Patient }) => Patient as Patient)
              .filter((patient) => !patient.Deleted?.Deleted);
            if (!controller.signal.aborted) {
              setPatients(list);
              setLoading(false);
            }
            controller.abort();
            break;
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          debug('[AttachPatientModal] search error', error);
          setPatients([]);
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [organizationID, query]);

  return { patients, loading };
};

export const AttachPatientModal: FC<AttachPatientModalProps> = (props) => {
  const { isOpen, onClose, onAttachPatient } = props;
  const { formatMessage } = useIntl();

  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const [inputValue, setInputValue] = useState('');
  const [attaching, setAttaching] = useState(false);
  const debouncedQuery = useDebounce(inputValue);

  const { patients, loading } = usePatientSearch(
    isOpen ? organizationID : undefined,
    debouncedQuery,
  );

  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setAttaching(false);
    }
  }, [isOpen]);

  const handlePick = async (patient: Patient) => {
    setAttaching(true);
    try {
      await onAttachPatient({ patientId: patient.ID });
      onClose();
    } catch {
      // Toast surfaces via useEncountersPage; keep the modal open so the
      // user can retry or pick a different patient.
      setAttaching(false);
    }
  };

  return (
    <Modal
      data-component="attach-patient-modal"
      isOpen={isOpen}
      onCancel={onClose}
      title={formatMessage({
        id: 'encounters.details.attachPatientModal.title',
        defaultMessage: 'Attach patient',
      })}
      hideFooter
      className={styles.modal}
    >
      <div className={styles.body}>
        <Input
          name="attach-patient-search"
          className={styles.search}
          value={inputValue}
          icon="search"
          inputMode="search"
          placeholder={formatMessage({
            id: 'patientList.filters.searchBarPlaceholderAll',
            defaultMessage: ' Search by Patient name or ID',
          })}
          onChange={(event) => setInputValue(event.target.value)}
          onResetField={() => setInputValue('')}
        />

        <div className={styles.list}>
          {loading ? (
            <div className={styles.state}>
              <Spinner />
            </div>
          ) : patients.length === 0 ? (
            <div className={styles.state}>
              {formatMessage({
                id: 'encounters.details.attachPatientModal.empty',
                defaultMessage: 'No patients found',
              })}
            </div>
          ) : (
            patients.map((patient) => {
              const patientName = getName(
                patient.PersonalData?.FirstName,
                patient.PersonalData?.LastName,
                { userLocale },
              );
              const dob = patient.PersonalData?.DateOfBirth;
              return (
                <button
                  key={patient.ID}
                  type="button"
                  className={styles.item}
                  disabled={attaching}
                  onClick={() => handlePick(patient)}
                >
                  <span className={styles.itemName}>
                    {patientName ||
                      formatMessage({
                        id: 'encounters.details.attachPatientModal.unnamed',
                        defaultMessage: 'Unnamed',
                      })}
                  </span>
                  {dob && <span className={styles.itemMeta}>{dob}</span>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};
